import React, { useState } from 'react';
import { X, RefreshCw, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import api from './axiosConfig';
import { useTranslation } from 'react-i18next';

const ReorderModal = ({ isOpen, onClose, data, onComplete }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const [rop, setRop] = useState(data?.reorderpoint || 0);
  const [safety, setSafety] = useState(data?.safetystock || 0);
  const [isSaving, setIsSaving] = useState(false);

  if (!isOpen) return null;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.post('/inventory/reorder-update', {
        record_id: `${data.itemnum}-${data.location}`,
        new_rop: parseFloat(rop),
        new_safety_stock: parseFloat(safety)
      });
      toast.success(t('reorderModal.updateSuccess'));
      if (onComplete) onComplete();
      onClose();
    } catch (err) {
      toast.error(t('reorderModal.updateFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[7000]" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-surface-card w-[400px] rounded shadow-2xl border border-line overflow-hidden animate-in zoom-in-95">
        {/* Header */}
        <div className="bg-primary-dark p-4 text-ink-inverse flex justify-between items-center">
          <div className="flex items-center gap-2">
            <RefreshCw size={16} className={isSaving ? "animate-spin" : ""} />
            <span className="font-black text-[10px] uppercase tracking-widest">{t('reorderModal.title')}</span>
          </div>
          <button onClick={onClose} className="hover:rotate-90 transition-transform"><X size={18}/></button>
        </div>

        <div className="p-6 space-y-4">
          {/* Info Box */}
          <div className={`bg-primary-soft p-3 rounded text-[10px] font-bold text-primary border-primary ${isRTL ? 'border-r-4' : 'border-l-4'}`}>
            {t('reorderModal.itemStore', { item: data.itemnum, store: data.location })}
          </div>

          {/* ROP Input */}
          <div className="space-y-1">
            <label className="text-[9px] font-black text-ink-secondary uppercase tracking-widest">{t('reorderModal.reorderPoint')}</label>
            <input
              type="number"
              className="w-full p-3 border-2 border-line-subtle focus:border-primary outline-none font-black text-primary transition-all"
              value={rop}
              onChange={(e) => setRop(e.target.value)}
            />
          </div>

          {/* Safety Stock Input */}
          <div className="space-y-1">
            <label className="text-[9px] font-black text-ink-secondary uppercase tracking-widest">{t('reorderModal.safetyStock')}</label>
            <input
              type="number"
              className="w-full p-3 border-2 border-line-subtle focus:border-primary outline-none font-black text-primary transition-all"
              value={safety}
              onChange={(e) => setSafety(e.target.value)}
            />
          </div>
        </div>

        <div className="p-4 bg-surface-panel flex justify-end gap-3 border-t border-line">
          <button onClick={onClose} className="text-[10px] font-bold text-ink-secondary uppercase">{t('common.cancel', 'Cancel')}</button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-primary text-ink-inverse px-8 py-2 rounded shadow-lg font-black text-[10px] uppercase hover:bg-primary-hover transition-all flex items-center gap-2"
          >
            {isSaving ? t('reorderModal.saving') : t('reorderModal.applyChanges')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReorderModal;
